import { motion } from "framer-motion";
import { FileText, HeartPulse, GraduationCap, ShieldCheck, Network, ArrowUpRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }),
};

const card =
  "rounded-3xl border-2 border-slate-900/5 bg-white shadow-[0_12px_40px_-15px_rgba(15,23,42,0.08)] hover:shadow-[0_20px_50px_-15px_rgba(255,87,34,0.15)] hover:-translate-y-1 transition-all duration-300 p-7 flex flex-col";

export default function BentoGridFeatures() {
  return (
    <section id="features" data-testid="bento-grid-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0} className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#2563EB]">The Toolbox</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Five powers. One people-first platform.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-6 gap-5 mt-12">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className={`${card} md:col-span-4 bg-gradient-to-br from-white to-orange-50`}>
            <div className="flex items-start justify-between gap-6">
              <div>
                <span className="inline-flex p-3 rounded-2xl bg-[#FF5722]/10 text-[#FF5722]"><FileText className="w-6 h-6" /></span>
                <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-4">AI Scribe</h3>
                <p className="text-base text-slate-600 leading-relaxed mt-2 max-w-md">
                  Claude drafts offer letters, PIPs, policies and reviews in seconds — precise,
                  on-tone, and always review-ready. HR paperwork at the speed of thought.
                </p>
              </div>
              <img
                src="https://images.unsplash.com/photo-1740252117012-bb53ad05e370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=400"
                alt="Milo, the Claude document architect character"
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-3xl object-cover border-2 border-slate-900/5 rotate-3 shrink-0"
              />
            </div>
            <div className="mt-auto pt-5 flex flex-wrap gap-2">
              {["Offer letters", "Policies", "PIPs", "Reviews"].map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-[#FF5722]/10 text-[#FF5722] text-xs font-bold">{t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} className={`${card} md:col-span-2`}>
            <span className="inline-flex p-3 rounded-2xl bg-[#EC4899]/10 text-[#EC4899] w-fit"><HeartPulse className="w-6 h-6" /></span>
            <h3 className="font-display text-lg sm:text-xl font-bold mt-4">Pulse Sentiment Analyzer</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">Weekly mood reads across every team, with early burnout signals before exit interviews happen.</p>
            <div className="mt-auto pt-4 flex items-end gap-1.5 h-14">
              {[40, 65, 50, 80, 62, 92, 74].map((h, i) => (
                <motion.div key={i} initial={{ height: 6 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08, duration: 0.6 }} className="flex-1 rounded-t-md bg-[#EC4899]/70" />
              ))}
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3} className={`${card} md:col-span-2`}>
            <span className="inline-flex p-3 rounded-2xl bg-[#2563EB]/10 text-[#2563EB] w-fit"><GraduationCap className="w-6 h-6" /></span>
            <h3 className="font-display text-lg sm:text-xl font-bold mt-4">Opera360 LMS Bridge</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">
              One click pushes new hires into Opera360's TRAIN tracks — 30/60/90 onboarding and certified curriculums.
            </p>
            <a href="https://www.opera360trains.com" target="_blank" rel="noopener noreferrer" className="mt-auto pt-4 inline-flex items-center gap-1 text-sm font-bold text-[#2563EB] hover:text-[#FF5722] transition-colors">
              opera360trains.com <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={4} className={`${card} md:col-span-2`}>
            <span className="inline-flex p-3 rounded-2xl bg-[#10B981]/10 text-[#10B981] w-fit"><ShieldCheck className="w-6 h-6" /></span>
            <h3 className="font-display text-lg sm:text-xl font-bold mt-4">Compliance Sentinel</h3>
            <p className="text-sm text-slate-600 leading-relaxed mt-2">Real-time fair-housing and policy watch. Flags risk before it becomes a settlement.</p>
            <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-bold text-[#10B981]">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse-dot" /> Monitoring 42 policy areas
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={5} className={`${card} md:col-span-2 bg-[#0F172A] text-white border-transparent`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="inline-flex p-3 rounded-2xl bg-white/10 text-[#F59E0B]"><Network className="w-6 h-6" /></span>
                <h3 className="font-display text-lg sm:text-xl font-bold mt-4">Smart Org Chart</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-2">A living map of every human, role, and reporting line — generated instantly.</p>
              </div>
              <img
                src="https://images.unsplash.com/photo-1728577740843-5f29c7586afe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=300"
                alt="Nova, the workforce culture character"
                className="w-16 h-16 rounded-2xl object-cover -rotate-3 shrink-0"
              />
            </div>
            <div className="mt-auto pt-4 font-mono2 text-xs text-slate-500">root → 8 regions → 214 humans ✦</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
